import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, setAccessToken, getAccessToken, onAccessTokenChange, API_BASE_URL } from '@/lib/api-client';
import { getDevRole, onDevRoleChange, type DevRole } from '@/lib/dev-role';

// NOTE: We use `window.location.href = '/login'` for session-boundary redirects
// rather than `useNavigate`, for two reasons:
//   1. AuthProvider sits *outside* BrowserRouter in App.tsx, so useNavigate
//      would throw "useNavigate must be used within a Router".
//   2. Full-reload semantics are actually desirable here — they guarantee
//      all in-memory React state from the prior session is wiped.

const DEV = import.meta.env.VITE_DEV_AUTH === 'true';

export type UserRole = 'user' | 'admin' | 'super_admin';

export interface User {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  createdAt: string;
  role: UserRole;
  onboardingCompleted?: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (accessToken: string) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  login: async () => {},
  logout: async () => {},
  isLoading: true,
});

interface RawMeResponse {
  id: string;
  email: string;
  name: string | null;
  avatar_url: string | null;
  created_at: string;
  role: UserRole;
  onboarding_completed?: boolean;
}

export function toUser(raw: RawMeResponse): User {
  return {
    id: raw.id,
    email: raw.email,
    name: raw.name,
    avatarUrl: raw.avatar_url,
    createdAt: raw.created_at,
    role: raw.role ?? 'user',
    onboardingCompleted: raw.onboarding_completed ?? false,
  };
}

const DEV_USER: User = {
  id: 'dev-user',
  email: 'dev@localhost',
  name: 'Dev User',
  avatarUrl: null,
  createdAt: new Date().toISOString(),
  role: 'super_admin',
  onboardingCompleted: false,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [hasToken, setHasToken] = useState<boolean>(() => getAccessToken() !== null);
  const [isLoading, setIsLoading] = useState(true);
  const qc = useQueryClient();

  // Keep latest `user` available in a ref so the token subscriber can read
  // it without re-subscribing on every render.
  const userRef = useRef<User | null>(null);
  useEffect(() => { userRef.current = user; }, [user]);

  // DEV-only simulated identity (#37). Drives the DEV-branch context value via
  // React state so toggling re-evaluates route gating with no hard reload.
  const [devRole, setDevRoleState] = useState<DevRole>(() => (DEV ? getDevRole() : 'super_admin'));
  useEffect(() => {
    if (!DEV) return;
    return onDevRoleChange((role) => {
      setDevRoleState(role);
      // New identity → drop the previous role's cached data so stale cross-role
      // results don't linger, and mirror the token to the role (anon = logged
      // out, no token). userRef stays null in DEV, but we null it explicitly so
      // setting the token to null here can never trip the implicit-logout
      // redirect (same guard logout() pre-empts).
      userRef.current = null;
      qc.clear();
      setAccessToken(role === 'anon' ? null : 'dev-token');
    });
  }, [qc]);

  // Implicit logout: token went null while we had a user → clear cache + redirect.
  useEffect(() => {
    return onAccessTokenChange((token) => {
      setHasToken(token !== null);
      if (token === null && userRef.current !== null) {
        setUser(null);
        qc.clear();
        window.location.href = '/login';
      }
    });
  }, [qc]);

  // Initial-mount silent refresh — if the user has a valid recost_refresh
  // cookie from a prior session, we wake up logged in.
  useEffect(() => {
    if (DEV) {
      // Token mirrors the simulated role: anon = logged out (no token), any
      // other role = placeholder so getAccessToken() is non-null. The mock
      // layer ignores the token's value either way.
      setAccessToken(getDevRole() === 'anon' ? null : 'dev-token');
      setIsLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (res.ok) {
          const body = await res.json() as { data: { token: string } };
          setAccessToken(body.data.token);
          const me = await apiClient.get<{ data: RawMeResponse }>('/auth/me');
          setUser(toUser(me.data));
        }
      } catch {
        // Network failure or 401 — treat as logged out.
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  async function login(accessToken: string) {
    qc.clear();
    setAccessToken(accessToken);
    setIsLoading(true);
    try {
      const me = await apiClient.get<{ data: RawMeResponse }>('/auth/me');
      setUser(toUser(me.data));
    } catch {
      setAccessToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }

  async function logout() {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Best-effort; clear local state regardless.
    }
    // Pre-empt userRef so the onAccessTokenChange subscriber's
    // implicit-logout guard fails — explicit logout owns the teardown.
    userRef.current = null;
    setAccessToken(null);
    setUser(null);
    qc.clear();
    window.location.href = '/login';
  }

  if (DEV) {
    // DEV identity comes from devRole, not the `user` state — the user/userRef
    // state and the login()/logout() setUser calls are inert on this path.
    const devUser = devRole === 'anon' ? null : { ...DEV_USER, role: devRole };
    return (
      <AuthContext.Provider value={{ isAuthenticated: devUser !== null, user: devUser, login, logout, isLoading: false }}>
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated: hasToken && !!user, user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
